/**
 * Slice 36 / 42 — Walk: fullscreen camera + review grid. Photos only.
 * Quick checks live on the separate Check step (after Walk, before Clean).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Camera, Check, Loader2, Video, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { useCreateMaintenanceReport } from "@/hooks/useMaintenanceReports";
import {
  WALK_MIN_PHOTOS,
  collectSimplePhotoUrls,
  readSimpleFlowState,
  type InspectFinding,
  type SimpleFlowChromeOpts,
  type SimpleFlowState,
} from "@/lib/simpleTurnover";
import {
  appendPhotoCaptureRecord,
  captureFrameFromVideo,
  uploadInAppCleaningPhoto,
  type CleaningPhotoCaptureRecord,
} from "@/lib/cleaningPhotoCapture";
import { simpleFlowSegmentIndex } from "@/lib/turnoverWizardPlan";
import { SimpleStepHeader } from "@/components/cleaner/SimpleStepHeader";
import { CLEANER_SIMPLE_CTA_FOOTER, CLEANER_SIMPLE_STEP_SHELL } from "@/lib/turnoverChrome";
import { isVideoMediaUrl, pickVideoRecorderMime } from "@/lib/mediaUrl";
import { MediaThumb } from "@/components/media/MediaThumb";
import { PhotoNeedNag } from "@/components/cleaner/PhotoNeedNag";

type Mode = "camera" | "review";
type CaptureKind = "photo" | "video";
type IssueType = "missing" | "broken";

const MAX_VIDEO_SECONDS = 45;

interface Props {
  propertyId: string;
  taskId: string;
  cleanerId?: string | null;
  checklistName?: string | null;
  entryWalkthroughState: unknown;
  includeMaintenance?: boolean;
  includeQuickCheck?: boolean;
  includeGuestSetup?: boolean;
  onComplete: () => void;
  onBack?: () => void;
  onSelectSegment?: (index: number) => void;
}

async function uploadWalkVideo(
  propertyId: string,
  taskId: string,
  blob: Blob,
  ext: string,
): Promise<string> {
  const contentType = blob.type || (ext === "mp4" ? "video/mp4" : "video/webm");
  const path = `${propertyId}/${taskId}/simple-before/walk/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from("cleaning-photos").upload(path, blob, {
    contentType,
    upsert: false,
  });
  if (error) throw error;
  const { data } = supabase.storage.from("cleaning-photos").getPublicUrl(path);
  return data.publicUrl;
}

function formatRecordTime(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function SimpleWalkStep({
  propertyId,
  taskId,
  cleanerId,
  checklistName,
  entryWalkthroughState,
  includeMaintenance = false,
  includeQuickCheck = false,
  includeGuestSetup = false,
  onComplete,
  onBack,
  onSelectSegment,
}: Props) {
  const { t } = useTranslation();
  const chrome: SimpleFlowChromeOpts = { includeMaintenance, includeQuickCheck, includeGuestSetup };
  const createReport = useCreateMaintenanceReport();
  const initial = readSimpleFlowState(entryWalkthroughState);
  const initialUrls = collectSimplePhotoUrls(initial, "before");
  const initialFindings = (initial.inspect_findings ?? []).filter(
    (f) => f.status === "missing" || f.status === "broken",
  );

  const [mode, setMode] = useState<Mode>(() =>
    initialUrls.length >= WALK_MIN_PHOTOS ? "review" : "camera",
  );
  const [captureKind, setCaptureKind] = useState<CaptureKind>("photo");
  const [urls, setUrls] = useState<string[]>(initialUrls);
  const [findings, setFindings] = useState<InspectFinding[]>(initialFindings);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);

  const [issueOpen, setIssueOpen] = useState(false);
  const [issueType, setIssueType] = useState<IssueType>("broken");
  const [issueNote, setIssueNote] = useState("");
  const [issuePhotoUrl, setIssuePhotoUrl] = useState<string | null>(null);
  /** Problem → photograph the issue first, then classify. */
  const [problemShoot, setProblemShoot] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const shutterLock = useRef(false);
  const pendingUploads = useRef(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordChunksRef = useRef<Blob[]>([]);
  const recordTimerRef = useRef<number | null>(null);
  const recordMimeRef = useRef(pickVideoRecorderMime());

  const canFinish = urls.length >= WALK_MIN_PHOTOS;
  const strip = useMemo(() => urls.slice(-4), [urls]);
  const stripOverflow = Math.max(0, urls.length - strip.length);
  const photoCountLabel =
    urls.length < WALK_MIN_PHOTOS && mode === "review"
      ? t("photoProgress").replace("{{count}}", String(urls.length)).replace("{{min}}", String(WALK_MIN_PHOTOS))
      : urls.length === 1
        ? t("photoCountOne").replace("{{count}}", String(urls.length))
        : t("photoCount").replace("{{count}}", String(urls.length));

  const persist = useCallback(
    async (patch: Partial<SimpleFlowState>) => {
      const prev = readSimpleFlowState(entryWalkthroughState);
      const merged: SimpleFlowState = { ...prev, simple_flow: true, ...patch };
      const { error } = await supabase
        .from("cleaning_tasks")
        .update({ entry_walkthrough_state: merged })
        .eq("id", taskId);
      if (error) throw error;
    },
    [entryWalkthroughState, taskId],
  );

  const stopCamera = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        /* ignore */
      }
    }
    mediaRecorderRef.current = null;
    if (recordTimerRef.current != null) {
      window.clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    setRecording(false);
    setRecordSecs(0);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(t("cameraUnavailableOpen"));
      return;
    }
    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: captureKind === "video",
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        video.setAttribute("playsinline", "true");
        video.muted = true;
        await video.play().catch(() => undefined);
      }
      setCameraReady(true);
    } catch (err) {
      console.error("[SimpleWalkStep] camera", err);
      const name = err instanceof Error ? err.name : "";
      setCameraError(
        name === "NotAllowedError"
          ? t("allowCameraKeepShooting")
          : t("couldNotOpenCameraTap"),
      );
      setCameraReady(false);
    }
  }, [captureKind, stopCamera]);

  useEffect(() => {
    if (mode === "camera") {
      void startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [mode, captureKind, startCamera, stopCamera]);

  const flashShutter = () => {
    setFlash(true);
    window.setTimeout(() => setFlash(false), 180);
  };

  const persistUrls = useCallback(
    async (nextUrls: string[], record?: CleaningPhotoCaptureRecord) => {
      const publicUrls = nextUrls.filter((u) => !u.startsWith("blob:"));
      const prev = readSimpleFlowState(entryWalkthroughState);
      await persist({
        before_photo_urls: publicUrls,
        photo_captures: record
          ? appendPhotoCaptureRecord(prev.photo_captures, record)
          : prev.photo_captures,
      });
    },
    [entryWalkthroughState, persist],
  );

  const uploadStillInBackground = (
    localUrl: string,
    frame: { blob: Blob; width: number; height: number },
  ): Promise<string> => {
    pendingUploads.current += 1;
    return (async () => {
      try {
        const { url, record } = await uploadInAppCleaningPhoto({
          propertyId,
          taskId,
          moment: "walk",
          blob: frame.blob,
          width: frame.width,
          height: frame.height,
          storagePath: `${propertyId}/${taskId}/simple-before/walk/${crypto.randomUUID()}.jpg`,
          locationTimeoutMs: 0,
        });
        let nextPublic: string[] = [];
        setUrls((prev) => {
          const next = prev.map((u) => (u === localUrl ? url : u));
          nextPublic = next.filter((u) => !u.startsWith("blob:"));
          return next;
        });
        URL.revokeObjectURL(localUrl);
        await persistUrls(nextPublic, record);
        return url;
      } catch (err) {
        setUrls((prev) => prev.filter((u) => u !== localUrl));
        URL.revokeObjectURL(localUrl);
        toast({
          title: t("captureFailed"),
          description: err instanceof Error ? err.message : t("tryAgain"),
          variant: "destructive",
        });
        throw err;
      } finally {
        pendingUploads.current = Math.max(0, pendingUploads.current - 1);
      }
    })();
  };

  const captureStill = async () => {
    if (shutterLock.current || recording) return;
    const video = videoRef.current;
    if (!video || !cameraReady) {
      toast({
        title: t("cameraRequired"),
        description: t("cameraRequiredDesc"),
        variant: "destructive",
      });
      void startCamera();
      return;
    }
    shutterLock.current = true;
    try {
      const frame = await captureFrameFromVideo(video);
      const localUrl = URL.createObjectURL(frame.blob);
      flashShutter();

      if (problemShoot) {
        setUrls((prev) => [...prev, localUrl]);
        setIssuePhotoUrl(localUrl);
        setProblemShoot(false);
        setIssueOpen(true);
        shutterLock.current = false;
        void uploadStillInBackground(localUrl, frame)
          .then((url) => {
            setIssuePhotoUrl((cur) => (cur === localUrl ? url : cur));
          })
          .catch(() => {
            setIssuePhotoUrl(null);
            setIssueOpen(false);
          });
        return;
      }

      setUrls((prev) => [...prev, localUrl]);
      shutterLock.current = false;
      void uploadStillInBackground(localUrl, frame);
    } catch (err) {
      shutterLock.current = false;
      toast({
        title: t("captureFailed"),
        description: err instanceof Error ? err.message : t("tryAgain"),
        variant: "destructive",
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
  };

  const startRecording = async () => {
    if (!streamRef.current || recording || busy) return;
    try {
      recordChunksRef.current = [];
      const mime = recordMimeRef.current;
      const recorder = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : undefined);
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        if (recordTimerRef.current != null) {
          window.clearInterval(recordTimerRef.current);
          recordTimerRef.current = null;
        }
        setRecording(false);
        setRecordSecs(0);
        const chunks = recordChunksRef.current;
        if (chunks.length === 0) return;
        setBusy(true);
        try {
          const blob = new Blob(chunks, { type: mime || "video/webm" });
          const ext = mime?.includes("mp4") ? "mp4" : "webm";
          const url = await uploadWalkVideo(propertyId, taskId, blob, ext);
          let nextUrls: string[] = [];
          setUrls((prev) => {
            nextUrls = [...prev, url];
            return nextUrls;
          });
          flashShutter();
          await persistUrls(nextUrls);
        } catch (err) {
          toast({
            title: t("couldntSaveVideo"),
            description: err instanceof Error ? err.message : t("tryPhotoInstead"),
            variant: "destructive",
          });
        } finally {
          setBusy(false);
        }
      };
      recorder.start(250);
      setRecording(true);
      setRecordSecs(0);
      recordTimerRef.current = window.setInterval(() => {
        setRecordSecs((s) => {
          if (s + 1 >= MAX_VIDEO_SECONDS) {
            stopRecording();
            return s + 1;
          }
          return s + 1;
        });
      }, 1000);
    } catch (err) {
      toast({
        title: t("couldntRecord"),
        description: err instanceof Error ? err.message : t("tryPhotoInstead"),
        variant: "destructive",
      });
    }
  };

  const onShutter = () => {
    if (captureKind === "video") {
      if (recording) stopRecording();
      else void startRecording();
      return;
    }
    void captureStill();
  };

  const removeAt = async (index: number) => {
    const nextUrls = urls.filter((_, i) => i !== index);
    setUrls(nextUrls);
    try {
      await persist({ before_photo_urls: nextUrls });
    } catch {
      /* keep local */
    }
  };

  const openProblem = () => {
    if (recording || shutterLock.current) return;
    if (mode !== "camera") setMode("camera");
    setCaptureKind("photo");
    setIssueType("broken");
    setIssueNote("");
    setIssuePhotoUrl(null);
    setIssueOpen(false);
    setProblemShoot(true);
  };

  const cancelProblemShoot = () => {
    setProblemShoot(false);
  };

  const retakeIssuePhoto = () => {
    setIssueOpen(false);
    setIssuePhotoUrl(null);
    setProblemShoot(true);
    if (mode !== "camera") setMode("camera");
  };

  const saveIssue = async () => {
    if (!issuePhotoUrl || issuePhotoUrl.startsWith("blob:")) {
      toast({ title: t("takePhotoOfProblem"), description: t("waitPhotoSaving") });
      return;
    }
    setBusy(true);
    try {
      const finding: InspectFinding = {
        status: issueType,
        note: issueNote.trim() || null,
        evidence_photo_url: issuePhotoUrl,
        reference_photo_url: null,
        reference_photo_id: null,
      };
      const next = [
        ...findings.filter((f) => f.evidence_photo_url !== issuePhotoUrl),
        finding,
      ];
      setFindings(next);
      const publicUrls = urls.filter((u) => !u.startsWith("blob:"));
      await persist({
        inspect_findings: next,
        before_photo_urls: publicUrls,
      });

      if (cleanerId) {
        try {
          await createReport.mutateAsync({
            property_id: propertyId,
            cleaning_task_id: taskId,
            cleaner_id: cleanerId,
            checklist_name: checklistName || undefined,
            room_name: "Walk",
            response: issueType === "broken" ? "needs_repair" : "no",
            notes: `${issueType.toUpperCase()}${issueNote.trim() ? `: ${issueNote.trim()}` : ""}`,
            photos: [issuePhotoUrl],
            task_description: issueType === "broken" ? "Broken / damage" : "Missing item",
          });
        } catch {
          /* finding still on task */
        }
      }

      setIssueOpen(false);
      setIssueNote("");
      setIssuePhotoUrl(null);
    } catch (err) {
      toast({
        title: t("couldNotSave"),
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const finishWalk = async () => {
    if (!canFinish || recording) {
      toast({ title: t("needNPhotos").replace("{{min}}", String(WALK_MIN_PHOTOS)), description: t("photoTheUnit") });
      return;
    }
    if (pendingUploads.current > 0 || urls.some((u) => u.startsWith("blob:"))) {
      toast({ title: t("waitPhotoSaving"), description: t("photosStillUploading") });
      return;
    }
    setBusy(true);
    try {
      await persist({
        before_photo_urls: urls,
        inspect_findings: findings,
        walk_complete: true,
        before_complete: true,
        inspect_complete: true,
      });
      stopCamera();
      onComplete();
    } catch (err) {
      toast({
        title: t("couldNotContinue"),
        description: err instanceof Error ? err.message : t("tryAgain"),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={cn(CLEANER_SIMPLE_STEP_SHELL, "relative text-[#0f1720]")}>
      <SimpleStepHeader
        title={t("walkTitle")}
        activeSegment={simpleFlowSegmentIndex("walk", chrome)}
        includeMaintenance={includeMaintenance}
        includeQuickCheck={includeQuickCheck}
        includeGuestSetup={includeGuestSetup}
        onBack={onBack}
        onSelectSegment={onSelectSegment}
        right={
          mode === "review" ? (
            <div
              className={cn(
                "text-[15px] font-bold",
                canFinish ? "text-[#15776d]" : "text-[#8494a2]",
              )}
            >
              {photoCountLabel}
            </div>
          ) : null
        }
      />

      {mode === "camera" ? (
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-[#1c2732]">
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            className={cn(
              "absolute inset-0 h-full w-full object-cover",
              !cameraReady && "opacity-0",
            )}
          />
          {!cameraReady ? (
            <div className="absolute inset-0 z-[1] flex flex-col items-center justify-center gap-3 px-6 text-center">
              {busy ? (
                <Loader2 className="h-8 w-8 animate-spin text-white" />
              ) : (
                <>
                  <Camera className="h-10 w-10 text-white/50" strokeWidth={1.8} />
                  <p className="text-[15px] font-medium text-white/70">
                    {cameraError || t("startingCamera")}
                  </p>
                  <button
                    type="button"
                    onClick={() => void startCamera()}
                    className="mt-1 rounded-full bg-[#24a89a] px-5 py-3 text-[15px] font-bold text-white"
                  >
                    {t("openCamera")}
                  </button>
                </>
              )}
            </div>
          ) : null}

          {recording ? (
            <div className="absolute left-4 top-[4.5rem] z-[2] flex items-center gap-2 rounded-full bg-[#dc2626] px-3 py-1.5 text-[13px] font-bold text-white">
              <span className="h-2 w-2 animate-pulse rounded-full bg-white" />
              {formatRecordTime(recordSecs)}
            </div>
          ) : null}

          {flash ? (
            <div className="pointer-events-none absolute inset-0 z-[3] animate-[walkFlash_0.28s_ease-out] bg-white" />
          ) : null}

          <div
            className={cn(
              "absolute left-4 top-4 z-[2] rounded-full px-4 py-2 text-[14.5px] font-bold text-white",
              problemShoot ? "bg-[#b4453a]" : "bg-[rgba(15,23,32,0.72)]",
            )}
          >
            {problemShoot ? t("photoTheProblem") : t("photoTheUnit")}
          </div>
          {problemShoot ? (
            <div className="absolute right-4 top-4 z-[2] max-w-[42%] rounded-full bg-[rgba(15,23,32,0.72)] px-3.5 py-2 text-right text-[12.5px] font-bold leading-snug text-white">
              {t("problemThenPhotoHint")}
            </div>
          ) : (
            <div className="absolute right-4 top-4 z-[2] flex rounded-full bg-[rgba(15,23,32,0.72)] p-1">
              {(
                [
                  { id: "photo" as const, label: t("photo") },
                  { id: "video" as const, label: t("video") },
                ]
              ).map((opt) => {
                const active = captureKind === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    disabled={recording || busy}
                    onClick={() => setCaptureKind(opt.id)}
                    className={cn(
                      "rounded-full px-3.5 py-1.5 text-[12.5px] font-bold",
                      active ? "bg-white text-[#0f1720]" : "text-[#9fb0bc]",
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}

          <div className="relative z-[1] mt-auto flex flex-col gap-3.5 bg-gradient-to-t from-[rgba(15,23,32,0.86)] to-transparent px-0 pb-6 pt-10">
            {strip.length > 0 ? (
              <div className="flex gap-2 overflow-hidden px-[18px]">
                {strip.map((url) => (
                  <div
                    key={url}
                    className="relative h-[58px] w-[58px] shrink-0 overflow-hidden rounded-xl border border-white/20"
                  >
                    <MediaThumb url={url} />
                    {isVideoMediaUrl(url) ? (
                      <div className="absolute inset-x-0 bottom-0 flex justify-center bg-black/55 py-0.5">
                        <Video className="h-3 w-3 text-white" strokeWidth={2.4} />
                      </div>
                    ) : null}
                  </div>
                ))}
                {stripOverflow > 0 ? (
                  <div className="flex h-[58px] w-[58px] shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-[14px] font-bold text-white">
                    +{stripOverflow}
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="flex items-center justify-between px-[18px]">
              {problemShoot ? (
                <button
                  type="button"
                  disabled={recording}
                  onClick={cancelProblemShoot}
                  className="flex h-12 min-w-[84px] items-center justify-center rounded-full border-[1.5px] border-white/35 px-3 text-[13px] font-bold text-white active:scale-95 disabled:opacity-40"
                >
                  {t("cancel")}
                </button>
              ) : (
                <button
                  type="button"
                  disabled={recording}
                  onClick={openProblem}
                  className="flex h-12 w-[84px] flex-col items-center justify-center gap-0.5 rounded-full border-[1.5px] border-[#f3c7c2] bg-[rgba(180,69,58,0.22)] text-[12px] font-bold leading-tight text-white active:scale-95 disabled:opacity-40"
                >
                  <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2.4} />
                  {t("problem")}
                </button>
              )}

              <button
                type="button"
                onClick={onShutter}
                aria-label={
                  problemShoot
                    ? t("photoTheProblem")
                    : captureKind === "video"
                      ? recording
                        ? t("stop")
                        : t("record")
                      : t("capture")
                }
                className={cn(
                  "flex h-[78px] w-[78px] items-center justify-center rounded-full border-[5px] active:scale-95 disabled:opacity-60",
                  recording
                    ? "border-[#dc2626]/70"
                    : problemShoot
                      ? "border-[#f3c7c2]"
                      : "border-white/30",
                )}
              >
                <span
                  className={cn(
                    "flex h-full w-full items-center justify-center rounded-full",
                    recording ? "bg-[#dc2626]" : problemShoot ? "bg-[#fdecea]" : "bg-white",
                  )}
                >
                  {recording ? (
                    <span className="h-7 w-7 rounded-[6px] bg-white" />
                  ) : captureKind === "video" && !problemShoot ? (
                    <span className="h-6 w-6 rounded-full bg-[#dc2626]" />
                  ) : problemShoot ? (
                    <AlertTriangle className="h-7 w-7 text-[#b4453a]" strokeWidth={2.4} />
                  ) : null}
                </span>
              </button>

              <div className="relative flex w-[84px] justify-end">
                {problemShoot ? (
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/12 text-[11px] font-bold leading-tight text-white/70">
                    {t("snap")}
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      disabled={urls.length === 0 || recording}
                      onClick={() => setMode("review")}
                      aria-label={t("doneShooting")}
                      className={cn(
                        "flex h-14 w-14 items-center justify-center rounded-full active:scale-95",
                        urls.length > 0
                          ? "bg-[#24a89a] text-white"
                          : "bg-white/12 text-[#5c6b78]",
                      )}
                    >
                      <Check className="h-6 w-6" strokeWidth={3} />
                    </button>
                    {urls.length > 0 ? (
                      <span className="absolute -right-1 -top-1 flex h-6 min-w-6 items-center justify-center rounded-full border-2 border-[#1c2732] bg-white px-1.5 text-[12px] font-bold text-[#0f1720]">
                        {urls.length}
                      </span>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-3.5">
            <div className="mb-3 flex items-baseline justify-between px-1">
              <div className="text-[15px] font-bold">{t("photoTheUnit")}</div>
              <div
                className={cn(
                  "text-[13px] font-bold",
                  canFinish ? "text-[#15776d]" : "text-[#8494a2]",
                )}
              >
                {photoCountLabel}
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-2 [-webkit-overflow-scrolling:touch]">
              <div className="grid grid-cols-2 gap-2.5">
                {urls.map((url, i) => (
                  <div
                    key={url}
                    className="relative h-32 overflow-hidden rounded-[14px] border border-[#e6edf1] bg-[#eff4f7]"
                  >
                    <MediaThumb url={url} />
                    {isVideoMediaUrl(url) ? (
                      <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 bg-black/55 py-0.5">
                        <Video className="h-3 w-3 text-white" strokeWidth={2.4} />
                      </div>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => void removeAt(i)}
                      className="absolute right-1.5 top-1.5 flex h-[26px] w-[26px] items-center justify-center rounded-full bg-[rgba(15,23,32,0.6)] text-white"
                      aria-label={t("remove")}
                    >
                      <X className="h-3 w-3" strokeWidth={3} />
                    </button>
                  </div>
                ))}
              </div>

              {findings.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-2 px-0.5">
                  {findings.map((f) => (
                    <div
                      key={`${f.evidence_photo_url}-${f.status}`}
                      className="flex h-8 items-center gap-1.5 rounded-full border border-[#f3c7c2] bg-[#fdecea] px-3 text-[12.5px] font-bold text-[#b4453a]"
                    >
                      <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2.4} />
                      <span className="max-w-[160px] truncate">
                        {f.status === "missing" ? t("missing") : t("broken")}
                        {f.note?.trim() ? ` · ${f.note.trim()}` : ""}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <div className={cn(CLEANER_SIMPLE_CTA_FOOTER, "flex flex-col gap-2.5 border-t border-[#e6edf1]/60 bg-white")}>
            <PhotoNeedNag propertyId={propertyId} taskId={taskId} />
            <button
              type="button"
              onClick={() => setMode("camera")}
              className="flex h-[54px] w-full items-center justify-center gap-2.5 rounded-[15px] border-[1.5px] border-[#cfd9de] bg-white text-[16.5px] font-bold text-[#0f1720] active:scale-[0.99]"
            >
              <Camera className="h-[19px] w-[19px]" strokeWidth={2} />
              {t("keepShooting")}
            </button>
            <button
              type="button"
              disabled={busy || !canFinish}
              onClick={() => void finishWalk()}
              className={cn(
                "flex h-14 w-full items-center justify-center rounded-[15px] text-[17.5px] font-bold",
                canFinish
                  ? "bg-[#24a89a] text-white hover:bg-[#1f958a] active:bg-[#1b8378]"
                  : "bg-[#cfd9de] text-white",
              )}
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("next")}
            </button>
          </div>
        </>
      )}

      {issueOpen ? (
        <div className="fixed inset-0 z-[80] flex flex-col justify-end">
          <button
            type="button"
            className="absolute inset-0 bg-[rgba(15,23,32,0.55)]"
            aria-label={t("close")}
            onClick={() => !busy && setIssueOpen(false)}
          />
          <div className="relative z-10 flex max-h-[100dvh] w-full flex-col gap-4 overflow-y-auto rounded-t-[26px] bg-white px-[18px] pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-5">
            <div className="mx-auto h-1 w-11 rounded-full bg-[#dde5eb]" />
            <div className="text-[22px] font-bold tracking-[-0.4px]">{t("whatHappened")}</div>
            <div className="flex gap-2.5">
              <button
                type="button"
                onClick={() => setIssueType("broken")}
                className={cn(
                  "flex h-[54px] flex-1 items-center justify-center rounded-[13px] border-[1.5px] text-[16.5px] font-bold",
                  issueType === "broken"
                    ? "border-[#24a89a] bg-[#e2eeed] text-[#15776d]"
                    : "border-[#e6edf1] bg-white text-[#64748b]",
                )}
              >
                {t("broken")}
              </button>
              <button
                type="button"
                onClick={() => setIssueType("missing")}
                className={cn(
                  "flex h-[54px] flex-1 items-center justify-center rounded-[13px] border-[1.5px] text-[16.5px] font-bold",
                  issueType === "missing"
                    ? "border-[#24a89a] bg-[#e2eeed] text-[#15776d]"
                    : "border-[#e6edf1] bg-white text-[#64748b]",
                )}
              >
                {t("missing")}
              </button>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={retakeIssuePhoto}
                className={cn(
                  "flex h-[60px] w-[60px] shrink-0 items-center justify-center overflow-hidden rounded-[13px] border-[1.5px]",
                  issuePhotoUrl
                    ? "border-[#24a89a] bg-[#eff4f7]"
                    : "border-dashed border-[#c6d2d9] bg-[#fbfdfd]",
                )}
              >
                {issuePhotoUrl ? (
                  <div className="h-full w-full">
                    <MediaThumb url={issuePhotoUrl} />
                  </div>
                ) : (
                  <Camera className="h-5 w-5 text-[#15776d]" strokeWidth={2} />
                )}
              </button>
              <div className="min-w-0 flex-1 text-[16px] font-medium leading-snug text-[#64748b]">
                {issuePhotoUrl ? t("photoRequiredRetake") : t("photoRequiredTap")}
              </div>
            </div>
            <input
              type="text"
              inputMode="text"
              autoComplete="off"
              enterKeyHint="done"
              value={issueNote}
              onChange={(e) => setIssueNote(e.target.value)}
              placeholder={t("oneLine")}
              className="h-[54px] w-full rounded-[13px] border-[1.5px] border-[#e6edf1] bg-[#f8fafb] px-4 text-base font-medium outline-none placeholder:text-[#94a3b0] focus:border-[#24a89a]"
              style={{ fontSize: 16 }}
            />
            <button
              type="button"
              disabled={busy || !issuePhotoUrl || issuePhotoUrl.startsWith("blob:")}
              onClick={() => void saveIssue()}
              className={cn(
                "flex h-14 w-full items-center justify-center rounded-[15px] text-[17.5px] font-bold",
                issuePhotoUrl && !issuePhotoUrl.startsWith("blob:")
                  ? "bg-[#24a89a] text-white"
                  : "bg-[#cfd9de] text-white",
              )}
            >
              {busy || (issuePhotoUrl?.startsWith("blob:") ?? false) ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                t("save")
              )}
            </button>
          </div>
        </div>
      ) : null}

      <style>{`@keyframes walkFlash { from { opacity: 0.85; } to { opacity: 0; } }`}</style>
    </div>
  );
}
